import { Box, Typography } from '@mui/material';

import { Dispatch } from 'react';
import { ModalAction } from '../../../model/modal.model';
import zeplinColor from '../../../theme/Color';
import CustomButton from '../CustomButton/CustomButton';
import CustomCard from '../CustomCard/CustomCard';
import CustomModal from '../CustomModal/CustomModal';

export default function CustomDeleteModal({
  isModalOpen,
  modalDispatch,
  onDelete,
  title,
  itemName,
  description,
  isDeleting = false,
}: {
  isModalOpen: boolean;
  modalDispatch: Dispatch<ModalAction>;
  onDelete: () => void;
  title: string;
  itemName?: string;
  description?: string;
  isDeleting?: boolean;
}) {
  return (
    <CustomModal
      handleClose={() => modalDispatch({ type: 'CLOSE', payload: 'delete' })}
      open={isModalOpen}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CustomCard
          styles={{
            borderRadius: '14px',
            maxWidth: '375px',
            padding: { xs: '20px', xl: '24px' },
            backgroundColor: zeplinColor.Neutral1,
            boxShadow: 'rgba(0, 0, 0, 0.15)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '22px', xl: '32px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <Typography variant='heading3' color={zeplinColor.BasicBlack}>
                {title}
              </Typography>
              <Typography variant='body2' color={zeplinColor.BasicBlack}>
                {itemName ? (
                  <>
                    Are you sure you want to delete <strong>{itemName}</strong>? This action cannot
                    be undone.
                  </>
                ) : description ? (
                  <>{description}</>
                ) : (
                  <>Are you sure you want to delete this item? This action cannot be undone.</>
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: '10px', xl: '16px' },
              }}
            >
              <CustomButton
                disabled={isDeleting}
                onClick={() => modalDispatch({ type: 'CLOSE', payload: 'delete' })}
                variant='outlined'
                color={'primary'}
                buttonVariant='responsiveButtonMedium'
                style={{
                  flex: 1,
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: '32px', xl: '48px' },
                  padding: { xs: '7px 12px', xl: '14px 24px' },
                  '&.Mui-focusVisible': { backgroundColor: zeplinColor.Neutral3 },
                  display: 'flex',
                  border: `1px solid ${zeplinColor.BasicBlack}`,
                  alignItems: 'center',
                  '&:hover': { backgroundColor: zeplinColor.Neutral3 },
                }}
                textStyle={{ color: zeplinColor.BasicBlack }}
                text={'Cancel'}
              />
              <CustomButton
                disabled={isDeleting}
                onClick={onDelete}
                variant='contained'
                color={'primary'}
                buttonVariant='responsiveButtonMedium'
                style={{
                  flex: 1,
                  borderRadius: { xs: '10px', xl: '14px' },
                  height: { xs: '32px', xl: '48px' },
                  padding: { xs: '7px 12px', xl: '14px 24px' },
                  '&.Mui-focusVisible': { backgroundColor: zeplinColor.ErrorHover },
                  '&:hover': { backgroundColor: zeplinColor.ErrorHover },
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: zeplinColor.Error,
                }}
                textStyle={{ color: zeplinColor.Neutral1 }}
                text={isDeleting ? 'Deleting...' : 'Delete'}
              />
            </Box>
          </Box>
        </CustomCard>
      </Box>
    </CustomModal>
  );
}
